import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import type { ChatExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import ChatMenuModal from '../../../containers/modals/chatmenu';

export type Buttons = Record<string, Button>;

export type Button = {
  button: string;
  nodeId: string;
  order?: number;
};

export type ChatMenuPayload = {
  buttons: Buttons;
  text: string;
};

type ChatMenuNodeAttributes = GenericNodeAttributes<
  ChatMenuPayload,
  ChatExtraData
>;
type ChatMenuNodeProps = GenericNodeProps<ChatMenuPayload, ChatExtraData>;

const translatePayload: ChatMenuNodeAttributes['translatePayload'] = (node) => {
  const { buttons } = node.payload;

  return Object.entries(buttons)
    .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
    .map(([key, { nodeId }]) => ({
      id: uuid(),
      label: key,
      connection: nodeId || uuid(),
    }));
};

const updateConnection: ChatMenuNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const { payload } = node;
  const buttons = { ...payload.buttons };
  const label = connection.connectionLabel;

  if (label && buttons[label]) {
    buttons[label] = {
      ...buttons[label],
      nodeId: connectionId,
    };
  }

  return {
    ...node,
    payload: {
      ...payload,
      buttons,
    },
  };
};

export const ChatMenuNodeAttributes: ChatMenuNodeAttributes = {
  iconName: 'channels',
  label: 'Menu',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: ChatMenuNode,
  ActionModal: ChatMenuModal,
};

export function ChatMenuNode({ node }: ChatMenuNodeProps): JSX.Element {
  const { payload } = node;
  const { buttons } = payload;

  const optionCount = Object.keys(buttons).length;
  const optionText = optionCount === 1 ? 'Option' : 'Options';

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="semibold" text={`${optionCount} ${optionText}`} />
          </TextEllipsis>
        ),
        [optionCount, optionText],
      )}
    </TextContent>
  );
}
